/*
 *Model for the recursive Conversation of a Topic
 */

define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "modules/topicsSplit/topic/topicResponse/topicCategories/topicCategoriesModel",
  "modules/topicsSplit/topic/topicResponse/topicAgentAssist/topicAgentAssistModel",
  "modules/topicsSplit/topic/topicResponse/topicAttributes/topicAttributesModel",
  "modules/topicsSplit/topic/topicResponse/topicResponseCampaignModal/topicResponseCampaignModel",
  "modules/topicsSplit/topic/topicResponse/topicResponseModel",
  "modules/header/kbMenu/kbMenuModel",
], function (
  Backbone,
  $,
  _,
  Messenger,
  Properties,
  TopicCategoriesModel,
  TopicAgentAssistModel,
  TopicAttributesModel,
  TopicResponseCampaignModel,
  TopicResponseModel,
  KbMenuModel,
) {
  "use strict";

  const TopicConversationModel = {};

  TopicConversationModel.responseReview = Backbone.Model.extend({
    initializeCustom: function (options) {
      this.templateId = options.templateId;
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return (
        Properties.rootURL +
        "/" +
        kbId +
        "/intents/responses/templates/" +
        this.templateId +
        "/markResponseReviewed"
      );
    },
  });

  TopicConversationModel.topicConversations = Backbone.Collection.extend({
    model: TopicConversationModel.topicConversation,
  });

  // noinspection JSUnusedGlobalSymbols
  TopicConversationModel.topicConversation = Backbone.Model.extend({
    idAttribute: "intentId",
    selectedResponses: [],
    disableAll: false,
    enableAll: false,
    needToDisable: false,
    needToEnable: false,
    defaults: {
      description: "",
      escalationType: null,
      condition: "",
      action: "",
      responseId: 0,
    },
    initializeCustom: function () {
      //collection may be made during the parse first
      if (!this.decisionCol) {
        //create a collection for decision choices
        this.decisionCol = new TopicConversationModel.topicConversations();
        this.decisionCol.parentConversation = this;
      }
      //when we add or remove a decision from this node, consider the node dirty
      this.decisionCol.on("add remove", (d) => {
        this.dirty = true;
        d.parentConversation = this;
      });
    },
    addDecision: function (decisionDesc) {
      const decisions = this.decisionCol;
      const newNode = new TopicConversationModel.topicConversation(
        { description: decisionDesc },
        { parse: true },
      );
      //set the decision topic for this node, based on the description
      newNode.changeDecisionTopic();
      decisions.add(newNode);
      //clear out escalation type since non-final nodes cannot have escalations
      this.set("escalationType", null);
      return newNode; //return newly created decision
    },
    //convenience function to insert privileged area role names into the data-rdprivileges-name attribute.
    //Does not modify the conversation object's response.  The returned string can be used for viewing the
    //response in the UI
    addPrivilegedRoleNamesToResponse: function (responseText) {
      if (responseText.length === 0) {
        return responseText;
      }
      //insert the name of any roles used in privileged areas by adding the data-rdprivilges-name attribute
      const privilegedAreaRoles = Messenger.reqres.request("kb:privileged:area:roles");
      const responseHTML = $(responseText);
      //for each privileged area
      responseHTML.find("div[data-rdprivileges]").each(function () {
        //get the roleIds of the roles used in this area
        const areaRoles = $(this).attr("data-rdprivileges");
        const areaRolesArray = areaRoles.split(",");
        //create the list of role names
        let roleNames = "";
        for (let i = 0; i < areaRolesArray.length; i++) {
          const roleMatch = privilegedAreaRoles.findWhere({
            roleId: parseInt(areaRolesArray[i].trim(), 10),
          });
          if (roleMatch) {
            roleNames += roleMatch.get("roleName") + ", ";
          } else {
            roleNames += $.t("privilegedArea.invalidRole");
          }
        }
        roleNames = roleNames.slice(0, -2); //slice out last comma
        //set the names list as an attribute of the privileged area div
        $(this).attr("data-rdprivileges-name", roleNames);
      });
      return responseHTML[0].outerHTML;
    },
    //loads the topic categories for this node of the conversation
    categories: function (options) {
      if (this.categoriesCol) {
        return this.categoriesCol;
      }
      const subModelOptions = {
        doNotFetch: false,
        modelClass: TopicCategoriesModel.topicCategories,
      };
      Object.assign(subModelOptions, options);
      //use the href for category codes, if it exists (doesn't exist in new conversation objects)
      if (this.get("categories")) {
        subModelOptions.url = this.get("categories").href;
      } else {
        subModelOptions.url = () => {
          const kbId = Messenger.reqres.request("active:kb");
          const responseId = this.get("responseId");
          if (responseId) {
            return `${Properties.rootURL}/${kbId}/intents/responses/${responseId}/categories`;
          }
          throw new Error("Cannot determine Topic Categories URL without a Response ID");
        };
        if (!this.get("responseId")) {
          subModelOptions.doNotFetch = true;
        }
      }
      this.categoriesCol = this.getSubModel2(this.categoriesCol, subModelOptions);
      return this.categoriesCol;
    },
    responseCampaigns: function (options) {
      if (this.responseCampaignsCol) {
        return this.responseCampaignsCol;
      }
      const intentId = this.get("intentId");
      const responseId = this.get("responseId");
      const kbId = Messenger.reqres.request("active:kb");
      const subModelOptions = {
        doNotFetch: false,
        modelClass: TopicResponseCampaignModel.topicResponseCampaigns,
      };
      Object.assign(subModelOptions, options);
      subModelOptions.url = () => {
        if (intentId && responseId) {
          return `${Properties.rootURL}/${kbId}/intents/${intentId}/responses/${responseId}/campaigns`;
        }
        throw new Error(
          "Cannot determine URL for Response Template Campaigns without Intent ID and Response ID",
        );
      };
      if (!intentId || !responseId) {
        subModelOptions.doNotFetch = true;
      }
      this.responseCampaignsCol = this.getSubModel2(this.responseCampaignsCol, subModelOptions);
      return this.responseCampaignsCol;
    },
    responseTemplates: function (options) {
      if (this.responseTemplatesCol) {
        return this.responseTemplatesCol;
      }
      const subModelOptions = {
        doNotFetch: false,
        modelClass: TopicResponseModel.responseTemplates,
      };
      Object.assign(subModelOptions, options);
      subModelOptions.url = () => {
        const kbId = Messenger.reqres.request("active:kb");
        const intentId = this.get("intentId");
        const responseId = this.get("responseId");
        if (intentId && responseId) {
          return `${Properties.rootURL}/${kbId}/intents/${intentId}/responses/${responseId}/templates`;
        }
        throw new Error(
          "Cannot determine URL for Response Templates without Intent ID and Response ID",
        );
      };
      if (!this.get("intentId") || !this.get("responseId")) {
        subModelOptions.doNotFetch = true;
      }
      this.responseTemplatesCol = this.getSubModel2(this.responseTemplatesCol, subModelOptions);
      return this.responseTemplatesCol;
    },

    validateEmbeddedContent: async function (
      touchPointId,
      languageId,
      responseText,
      kbId,
      intentId,
    ) {
      const responseTemplate = this.findResponseTemplate(touchPointId, languageId);
      if (responseTemplate) {
        const responseId = this.get("responseId");
        return responseTemplate.validateEmbeddedContent(responseText, responseId, kbId, intentId);
      } else {
        throw new Error("Cannot find Response Template");
      }
    },
    topicAttributes: function (options) {
      if (this.topicAttributeList) {
        return this.topicAttributeList;
      }
      const subModelOptions = {
        doNotFetch: false,
        modelClass: TopicAttributesModel.topicAttributes,
      };
      Object.assign(subModelOptions, options);
      if (this.get("attributes")) {
        subModelOptions.url = this.get("attributes").href;
      } else {
        subModelOptions.url = () => {
          const kbId = Messenger.reqres.request("active:kb");
          const responseId = this.get("responseId");
          if (responseId) {
            return `${Properties.rootURL}/${kbId}/intents/responses/${responseId}/attributes`;
          }
          throw new Error("Cannot determine Topic Attributes URL without Response ID");
        };
        if (!this.get("responseId")) {
          subModelOptions.doNotFetch = true;
        }
      }
      this.topicAttributeList = this.getSubModel2(this.topicAttributeList, subModelOptions);
      return this.topicAttributeList;
    },
    addEmptyResponseTemplate(data) {
      let statusId = this.get("statusId");
      if (!statusId) {
        statusId = Messenger.reqres.request("current:topic:status");
        if (!statusId) {
          statusId = Messenger.reqres.request("default:topic:status");
        }
      }
      return this.responseTemplates().addResponseTemplate(
        Object.assign(
          {
            response: TopicResponseModel.responseTemplate.EMPTY_RESPONSE_TEXT,
            statusId: statusId,
          },
          data,
        ),
      );
    },
    //set the decision choice that matches the description of the conversation node.
    //Uses the new/changed description field to find a matching decision choice.
    //If an existing decision choice does not exist, then it will create a new one and add it to the
    //decision choice collection
    changeDecisionTopic: function () {
      //if there is a preexisting new decision model, decrement the usage count
      if (this.decisionModel && this.decisionModel.isNew()) {
        this.decisionModel.timesInUse--;
      }
      //get the current english decision label from the conversation description (where we store a copy of the
      // english label for convenience)
      const label = this.get("description");
      //get decision choices
      const decisions = Messenger.reqres.request("topic:decisions");
      const decisionMatch = decisions.findWhere({ intentName: label });
      if (decisionMatch) {
        console.log("match: " + decisionMatch.get("intentName"));
        this.decisionModel = decisionMatch;
      } else {
        console.log("No match for: " + label);
        //creating new decision topic and adding it to decisions collection
        const newDecisionTopic = Messenger.reqres.request("new:topic:decision", label);
        decisions.add(newDecisionTopic);
        this.decisionModel = newDecisionTopic;
      }
      //increment the usage count of this decision model, if new (for use later when determining if it needs to be
      // saved)
      if (this.decisionModel.isNew()) {
        this.decisionModel.timesInUse++;
      }
    },
    deleteActionGroup: function (touchPoint, language) {
      const responseTemplate = this.findResponseTemplate(touchPoint, language);
      if (responseTemplate) {
        if (responseTemplate.get("actionGroupId")) {
          responseTemplate.set("actionGroupId", 0);
          responseTemplate.saveModelPromise().then(() => {
            //fire change event
            this.trigger("change");
            //fire response changed event
            Messenger.vent.trigger("action:group:changed");
          });
        }
      }
    },
    deleteAssociatedContent: function () {
      // Delete the object property, so it is undefined. Returns true if
      // successful, false otherwise.
      const result = delete this.associatedContent;
      if (result) {
        this._setAssociatedContentOnResponseTemplates(null);
        //traverse the conversation recursively, deleting the associated content
        this.decisionCol.each((decision) => {
          decision._setAssociatedContentOnResponseTemplates(null);
        });
        this.responseTemplates()
          .saveCollectionPromise()
          .then(() => {
            this.trigger("change");
            // Fire response changed event
            Messenger.vent.trigger("topic:response:changed");
          });
      }
      return result;
    },
    deleteModel: function () {
      //delete the topic Conversation model by removing it from the collection
      if (this.collection) {
        this.collection.remove(this);
      }
      if (this.decisionModel && this.decisionModel.isNew()) {
        this.decisionModel.timesInUse--;
      }
    },
    epcMigrations: function (options) {
      if (this.epcMigrationsCol) {
        return this.epcMigrationsCol;
      }
      const subModelOptions = {
        doNotFetch: false,
        modelClass: TopicAgentAssistModel.ePCCategoryCodeMigrations,
      };
      Object.assign(subModelOptions, options);
      //use the href for epc codes, if it exists (doesn't exist in new conversation objects)
      if (this.get("epcMigrations")) {
        const epcMigrationsHref = this.get("epcMigrations").href;
        subModelOptions.url = () => epcMigrationsHref;
      } else {
        subModelOptions.url = () => {
          const kbId = Messenger.reqres.request("active:kb");
          const responseId = this.get("responseId");
          if (responseId) {
            return `${Properties.rootURL}/${kbId}/epc/${responseId}/migrations`;
          }
          throw new Error("Cannot determine EPC migrations URL without Response ID");
        };
        if (!this.get("responseId")) {
          subModelOptions.doNotFetch = true;
        }
      }
      this.epcMigrationsCol = this.getSubModel2(this.epcMigrationsCol, subModelOptions);
      return this.epcMigrationsCol;
    },
    getActionGroup: function (touchPoint, language) {
      const responseTemplate = this.findResponseTemplate(touchPoint, language);
      if (responseTemplate && responseTemplate.get("actionGroupId")) {
        return Messenger.reqres.request("action:group", responseTemplate.get("actionGroupId"));
      }
      return null;
    },
    //gets the associated content model for this conversation
    //if there is no associated content, then it returns null
    getAssociatedContent: function () {
      //first, check if the associated content object already exists
      if (this.associatedContent) {
        return this.associatedContent;
      }
      //otherwise, we need to search the responses and check for associated content
      const responseTemplates = this.responseTemplates();
      const responseTemplate = responseTemplates.find((r) => r.get("associatedContentId") > 0);
      //we found associated content, so we assume it applies to all responses
      //if we found associated content, fetch it
      if (responseTemplate) {
        this.associatedContent = Messenger.reqres.request(
          "content",
          responseTemplate.get("associatedContentId"),
        );
        return this.associatedContent;
      }
      return null;
    },
    getPath: function () {
      const path = [this.get("description")];
      let parent = this.parentConversation;
      while (parent) {
        path.unshift(parent.get("description"));
        parent = parent.parentConversation;
      }
      return path.join("  ");
    },
    getResponse: function (touchPoint, language) {
      const responseTemplate = this.findResponseTemplate(touchPoint, language);
      if (responseTemplate) {
        return responseTemplate.get("response");
      }
      //at this point, the template doesn't exist, so just return an empty string
      return "";
    },
    getResponseHistory: function (touchPoint, language) {
      const responseTemplate = this.findResponseTemplate(touchPoint, language);
      let History = Backbone.Collection.extend({
        url: function () {
          const kbId = Messenger.reqres.request("active:kb");
          return (
            Properties.rootURL +
            "/" +
            kbId +
            "/intents/responses/templates/" +
            this.templateId +
            "/history"
          );
        },
      });
      if (responseTemplate) {
        //check if response template has a templateId
        const templateId = responseTemplate.get("templateId");
        if (templateId) {
          //fetch the response history
          let history = new History();
          history.templateId = templateId;
          history.fetch();
          return history;
        }
      }
      //else we can't find the template, so just return an empty collection
      return new Backbone.Collection();
    },
    markResponseAsReviewed: function (touchpoint, language) {
      const responseTemplate = this.findOrAddResponseTemplate(touchpoint, language);
      let responseReviewModel = new TopicConversationModel.responseReview({
        templateId: responseTemplate.get("templateId"),
      });
      if (responseTemplate.get("templateId")) {
        responseReviewModel.save(null, {});
      }
      return responseReviewModel;
    },
    parse: function (response, options) {
      this._parseDecisions(response, options);
      this._parseResponses(response, options);
      return response;
    },
    _parseDecisions: function (response, options) {
      //override parse function (see backbone docs) in order to convert decisions array to a backbone collection
      //add each decision, parsing each model as we do
      if (response && response.decisions) {
        if (this.decisionCol && this.decisionCol.length == response.decisions.length) {
          for (let i = 0; i < response.decisions.length; ++i) {
            const existingDecision = this.decisionCol.at(i);
            const decisionResponse = response.decisions[i];
            // This roughly mimics what normally happens in BackboneJS when a Model is saved
            const decisionAttributes = existingDecision.parse(decisionResponse, options);
            if (
              _.isObject(decisionAttributes) &&
              existingDecision.set(decisionAttributes, options)
            ) {
              existingDecision.dirty = false;
              existingDecision.trigger("sync", existingDecision, decisionResponse, options);
            }
          }
        } else {
          const newDecisions = [];
          for (let i = 0; i < response.decisions.length; i++) {
            const newDecision = new TopicConversationModel.topicConversation(
              response.decisions[i],
              { parse: true },
            );
            //get the decision topic associated with the decision
            newDecision._getDecisionTopic();
            newDecisions.push(newDecision);
          }
          if (!this.decisionCol) {
            //create a collection for decision choices
            this.decisionCol = new TopicConversationModel.topicConversations(newDecisions);
            this.decisionCol.parentConversation = this;
          } else {
            this.decisionCol.reset(newDecisions);
          }
        }
        delete response.decisions;
      }
    },
    _parseResponses: function (response, options) {
      // Cannot parse the responses returned by the server when saving,
      // otherwise the existing responses will be overwritten by old server
      // data before they can be saved
      if (response.responses && !(options && options.saving)) {
        const responseTemplatesCol = this.responseTemplates({ doNotFetch: true });
        const responseTemplates = [];
        for (let i = 0; i < response.responses.length; ++i) {
          const responseTemplate = responseTemplatesCol.createResponseTemplate(
            response.responses[i],
          );
          responseTemplates.push(responseTemplate);
        }
        responseTemplatesCol.reset(responseTemplates, {
          silent: true,
        });
        delete response.responses;
      }
    },
    //convenience function to remove privileged area role names from privileged areas.
    //Does not modify the conversation object's response.  The returned string can be used to update
    //the conversation object because it no longer contains the temporary data-rdprivileges-name attribute
    removePrivilegedRoleNamesFromResponse: function (responseText) {
      //for each privileged area
      const responseHTML = $(responseText);
      responseHTML.find("div[data-rdprivileges]").each(function () {
        //remove the attribute data-rdprivileges-name
        $(this).removeAttr("data-rdprivileges-name");
      });
      return responseHTML[0].outerHTML;
    },
    removeImageDataWidgetFromResponse: function (responseText) {
      const responseHTML = $(responseText);
      responseHTML.find("a[data-widget=image]").each(function () {
        //remove the attribute data-widget from a tag
        $(this).removeAttr("data-widget");
      });
      return responseHTML[0].outerHTML;
    },
    setActionGroup: function (touchPointId, languageId, actionGroup) {
      const responseTemplate = this.findResponseTemplate(touchPointId, languageId);
      if (responseTemplate) {
        if (responseTemplate.get("actionGroupId") !== actionGroup.id) {
          //make sure that the action group was updated
          responseTemplate.set("actionGroupId", actionGroup.id); //set action group
          responseTemplate.saveModelPromise().then(() => {
            //fire change event
            this.trigger("change");
            //fire response changed event
            Messenger.vent.trigger("action:group:changed");
          });
        }
        return;
      }
      //if we reach this point, then this is a new response template, so add a new one (response text will be empty)
      this.addEmptyResponseTemplate({
        touchpointId: touchPointId,
        languageId: languageId,
        actionGroupId: actionGroup.id,
      });
      this.responseTemplates()
        .saveCollectionPromise()
        .then(() => Messenger.vent.trigger("action:group:changed"));
    },
    setAssociatedContent: function (content) {
      this.associatedContent = content;
      this._applyAssociatedContent();
      this.responseTemplates()
        .saveCollectionPromise()
        .then(() => {
          //fire change event
          this.trigger("change");
          //fire response changed event
          Messenger.vent.trigger("topic:response:changed");
        });
    },
    setEscalation: function (type) {
      //sets the escalation type unless it is set to 'none', it which case it saves as null
      if (type === "None") {
        this.set("escalationType", null);
      } else {
        this.set("escalationType", type);
      }
    },
    setResponse: function (touchPointId, languageId, responseText, { silent = false } = {}) {
      const kb = Messenger.reqres.request("active:kb:model");
      const responseTemplates = this.responseTemplates();
      const responseTemplate = this.findResponseTemplate(touchPointId, languageId);
      if (responseTemplate) {
        if (responseTemplate.get("response") !== responseText) {
          responseTemplate.set("response", responseText);
          this._markResponseTemplateAsPendingIfNeeded(kb, responseTemplate);
          //fire response changed event
          if (!silent) {
            Messenger.vent.trigger("topic:response:changed");
          }
        }
        return responseTemplate;
      }
      //if we reach this point, then this is a new response template, so add a new one
      const newResponseTemplate = responseTemplates.createResponseTemplate({
        touchpointId: touchPointId,
        languageId: languageId,
        response: responseText,
      });
      if (!newResponseTemplate.isEmpty()) {
        //only save a new response if it actually contains anything
        const topicStatusId = this.get("statusId");
        if (
          (kb.get("responseVersioning") &&
            topicStatusId == KbMenuModel.statuses.VERSIONED_PUBLISHED) ||
          topicStatusId == KbMenuModel.statuses.VERSIONED_DISABLED
        ) {
          newResponseTemplate.set("statusId", KbMenuModel.statuses.VERSIONED_PENDING_RESPONSE);
          this.set("statusId", KbMenuModel.statuses.VERSIONED_PENDING_RESPONSE);
        } else {
          newResponseTemplate.set("statusId", topicStatusId);
        }
        responseTemplates.push(newResponseTemplate);
        if (!silent) {
          Messenger.vent.trigger("topic:response:changed");
        }
        return newResponseTemplate;
      }
      return null;
    },
    replaceResponseTemplateModel: function (touchPointId, languageId, newResponseTemplate) {
      let responseTemplate = this.findResponseTemplate(touchPointId, languageId);
      if (responseTemplate) {
        this.responseTemplatesCol.remove(responseTemplate);
        this.responseTemplatesCol.addResponseTemplate(newResponseTemplate.toJSON());
      }
    },
    saveModel: function (options) {
      //save any new decision choices that may be used in this conversation
      const decisionChoices = Messenger.reqres.request("topic:decisions");
      if (decisionChoices !== undefined) {
        //if it's null, the user doesn't have permissions to load decisions (reviewer), and thus will not have modified any
        decisionChoices.saveCollection({
          success: () => {
            this._saveConversation(options); //continue saving the conversation, if successful
          },
          error: this._createSaveModelError(options),
        });
      } else {
        this._saveConversation(options); //continue saving the conversation, if successful.  there will be no
        // decision choices since the user cannot add any
      }
    },
    toJSON: function (options) {
      if (options && (options.parse || options.saving)) {
        const modelJson = _.clone(this.attributes);
        if (this.decisionCol) {
          modelJson.decisions = this.decisionCol.toJSON(options);
        } else {
          modelJson.decisions = [];
        }
        delete modelJson.responses;
        return modelJson;
      }
      return _.clone(this.attributes);
    },
    _validateDuplicateDescription: function (value) {
      if (this.collection) {
        let duplicate = false;
        const lowerCaseValue = value.toLowerCase();
        this.collection.each((node) => {
          if (node !== this && node.get("description").toLowerCase() === lowerCaseValue) {
            duplicate = true;
          }
        });
        if (duplicate) {
          return $.t("topic.decision.duplicateMessage");
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "description") {
        return this.validateRequiredValue(value) || this._validateDuplicateDescription(value);
      }
    },
    /**
     * Walks through all the response templates in a branching conversation and calls the iterator
     * for each response template.
     *
     * @param iter The iterator function to call for each response template.
     * @param scope The scope in which to call the iterator function.  If not set, then this conversation will
     * be used.
     */
    walkAllResponseTemplates: function (iter, scope) {
      scope = scope || this;

      const walkConversation = (conversation) => {
        if (conversation.responseTemplatesCol) {
          conversation.responseTemplatesCol.each((responseTemplate) =>
            iter.call(scope, responseTemplate),
          );
        }
        if (conversation.decisionCol) {
          conversation.decisionCol.each(walkConversation);
        }
      };

      walkConversation(this);
    },
    _applyAssociatedContent: function () {
      // If associated content has been loaded, apply contentId to each response.
      // If there is no associated content, that means that either there isn't
      // any associated content linked to this response, or that the response
      // hasn't been viewed in the tool (e.g. a branch).
      // Either way, we should leave it alone.
      if (this.associatedContent) {
        this._setAssociatedContentOnResponseTemplates(this.associatedContent);
      }
      //traverse the conversation recursively, applying the associated content
      this.decisionCol.each((decision) => {
        decision._applyAssociatedContent();
      });
    },
    _applyDecisionTopics: function () {
      //assign the intentId of the connected decision topic to the conversation model (id now exists for newly
      // saved decisions)
      if (this.decisionModel) {
        this.set("intentId", this.decisionModel.get("intentId"));
      }
      //recursively apply decision topics to child nodes
      this.decisionCol.each((decision) => {
        decision._applyDecisionTopics();
      });
    },
    _collectSaveSubModels: function () {
      const subModels = this.subModels ? [...this.subModels] : [];

      const addDecisionSubModels = (decision) => {
        if (decision && decision.subModels) {
          subModels.push(...decision.subModels);
        }
        if (decision && decision.decisionCol) {
          decision.decisionCol.each(addDecisionSubModels);
        }
      };

      if (this.decisionCol) {
        this.decisionCol.each(addDecisionSubModels);
      }
      return subModels;
    },
    _disableConversation: function (options) {
      const kbId = Messenger.reqres.request("active:kb");
      const intentId = this.get("intentId"); //get intent id from working conversation
      const disableAll = this.disableAll;
      const templateIds = this._getSelectedTemplates();
      const disableOptions = _.extend({}, options, {
        contentType: "application/json",
        error: (m, r) => {
          options.error(m, r);
        },
        success: (m) => {
          const updateResponseTemplateStatuses = (conversation, responses, decisions) => {
            const responseTemplates = conversation.responseTemplates();
            _.each(responses, (response) => {
              const templateId = response.templateId;
              const responseTemplate = responseTemplates.get(templateId);
              if (responseTemplate) {
                responseTemplate.set("statusId", response.statusId);
              }
            });
            _.each(decisions, (d) => {
              const decision = conversation.decisionCol.get(d.intentId);
              if (decision) {
                updateResponseTemplateStatuses(decision, d.responses, d.decisions);
              }
            });
          };

          updateResponseTemplateStatuses(this, m.get("responses"), m.get("decisions"));
          this.set("statusId", m.get("statusId"));
          this.set("statusVersion", m.get("statusVersion"));
          options.success();
        },
      });
      this.needToDisable = false;
      this.disableAll = false;
      this.selectedResponses = [];
      const model = new Backbone.Model({
        id: this.id,
      });
      if (disableAll) {
        disableOptions.url =
          Properties.rootURL + "/" + kbId + "/intents/" + intentId + "/conversation/disable";
      } else if (templateIds && templateIds.length > 0) {
        disableOptions.url =
          Properties.rootURL +
          "/" +
          kbId +
          "/intents/" +
          intentId +
          "/conversation/disable/responseTemplates";
        disableOptions.data = JSON.stringify(templateIds);
      }
      model.save(null, disableOptions);
    },
    _enableConversation: function (options) {
      const kbId = Messenger.reqres.request("active:kb");
      const intentId = this.get("intentId"); //get intent id from working conversation
      const enableAll = this.enableAll;
      const templateIds = this._getSelectedTemplates();
      const enableOptions = _.extend({}, options, {
        contentType: "application/json",
        error: (m, r) => {
          options.error(m, r);
        },
        success: (m) => {
          const updateResponseTemplateStatuses = (conversation, responses, decisions) => {
            const responseTemplates = conversation.responseTemplates();
            _.each(responses, (response) => {
              const templateId = response.templateId;
              const responseTemplate = responseTemplates.get(templateId);
              if (responseTemplate) {
                responseTemplate.set("statusId", response.statusId);
              }
            });
            _.each(decisions, (d) => {
              const decision = conversation.decisionCol.get(d.intentId);
              if (decision) {
                updateResponseTemplateStatuses(decision, d.responses, d.decisions);
              }
            });
          };

          updateResponseTemplateStatuses(this, m.get("responses"), m.get("decisions"));
          this.set("statusId", m.get("statusId"));
          this.set("statusVersion", m.get("statusVersion"));
          options.success();
        },
      });
      this.needToEnable = false;
      this.enableAll = false;
      this.selectedResponses = [];
      const model = new Backbone.Model({
        id: this.id,
      });
      if (enableAll) {
        enableOptions.url =
          Properties.rootURL + "/" + kbId + "/intents/" + intentId + "/conversation/enable";
      } else if (templateIds && templateIds.length > 0) {
        enableOptions.url =
          Properties.rootURL +
          "/" +
          kbId +
          "/intents/" +
          intentId +
          "/conversation/enable/responseTemplates";
        enableOptions.data = JSON.stringify(templateIds);
      }
      model.save(null, enableOptions);
    },
    findResponseTemplate: function (touchpointId, languageId) {
      return this.responseTemplates().findWhere({
        touchpointId: touchpointId,
        languageId: languageId,
      });
    },
    findOrAddResponseTemplate: function (touchpointId, languageId, initialData) {
      let responseTemplate = this.findResponseTemplate(touchpointId, languageId);
      if (!responseTemplate) {
        responseTemplate = this.addEmptyResponseTemplate(
          Object.assign(
            {
              languageId: languageId,
              touchpointId: touchpointId,
            },
            initialData,
          ),
        );
      }
      return responseTemplate;
    },
    //used to get a decision topic model by the intentId of this conversation branch
    //only used by the parse function when loading an existing conversation
    _getDecisionTopic: function () {
      if (!this.decisionModel) {
        //request the decision topics
        const decisionTopics = Messenger.reqres.request("topic:decisions");
        //get the decision intent for this branch and add it as a parameter to the model
        if (decisionTopics) {
          if (decisionTopics.isSynced) {
            this._setDecisionTopic(decisionTopics);
          } else {
            //wait until the decision intents are synced
            decisionTopics.once("sync", () => {
              this._setDecisionTopic(decisionTopics);
              //fire change event to notify any listeners
              this.trigger("change");
            });
          }
        }
      }
      return this.decisionModel;
    },
    _markResponseTemplateAsPendingIfNeeded: function (kb, responseTemplate) {
      const responseStatus = responseTemplate.get("statusId");
      if (
        (kb.get("responseVersioning") &&
          responseStatus == KbMenuModel.statuses.VERSIONED_PUBLISHED) ||
        responseStatus == KbMenuModel.statuses.VERSIONED_DISABLED
      ) {
        responseTemplate.set("statusId", KbMenuModel.statuses.VERSIONED_PENDING_RESPONSE);
      }
    },
    _setDecisionTopic: function (decisionTopics) {
      this.decisionModel = decisionTopics.findWhere({ intentId: this.get("intentId") });
      //make sure that the decision name is set as the description of the conversation (a legacy concern where
      // decision labels were separate)
      this.set("description", this.decisionModel.get("intentName"));
    },
    _isConversationDirty: function () {
      // recursively check if the conversation is dirty until we find a dirty node,
      // or we reach the end of the tree
      if (this.isNew() || this.dirty) {
        return true;
      } else {
        let dirty = false;
        this.decisionCol.each((decision) => {
          dirty = dirty || decision._isConversationDirty();
        });
        return dirty;
      }
    },
    _getSelectedTemplates: function () {
      if (!this.selectedResponses || this.selectedResponses.length === 0) {
        return [];
      }
      const templateIdList = [];
      this.walkAllResponseTemplates((responseTemplate) => {
        if (!responseTemplate.isNew()) {
          const testId =
            responseTemplate.get("languageId") + "_" + responseTemplate.get("touchpointId");
          if (_.contains(this.selectedResponses, testId)) {
            templateIdList.push(responseTemplate.get("templateId"));
          }
        }
      });
      return templateIdList;
    },
    _saveConversation: function (options) {
      //set any associated content values to the responses (make sure all responses have the same content)
      this._applyAssociatedContent();
      //recursively set the decision choice topic used in this conversation
      this._applyDecisionTopics();
      //Need to figure out if a branching conversation is dirty later in the conversation
      this.dirty = this._isConversationDirty();
      const saveSubModelOptions = _.extend({}, options, {
        saving: true,
        success: (m, r) => {
          this._saveConversationSuccess(m, r, options);
        },
      });
      if (this.dirty) {
        return this.save(null, {
          saving: true,
          success: () => {
            this.saveSubModels(saveSubModelOptions); //save categories and epc migrations
          },
          error: (model, xhr) => {
            Messenger.setErrorMsgFromResponse(xhr);
            if (options && options.error) {
              options.error(this, xhr, options);
            }
          },
        });
      } else {
        this.saveSubModels(saveSubModelOptions);
      }

      // Category list is cleared out when saving conversation.  Tell it to reacquire categories
      Messenger.vent.trigger("topic:category:list:refreshOnSave");
    },
    _saveConversationSuccess: function (model, response, options) {
      const saveConversationOptions = _.extend(
        {
          error: (m, r) => {
            Messenger.setErrorMsgFromResponse(r);
            Messenger.saveAttempted(false);
          },
          success: () => {
            Messenger.saveAttempted(true);
          },
        },
        options,
      );
      if (this.needToDisable) {
        this._disableConversation(saveConversationOptions);
      } else if (this.needToEnable) {
        this._enableConversation(saveConversationOptions);
      } else {
        saveConversationOptions.success(model, response, options);
      }
    },
    _setAssociatedContentOnResponseTemplates: function (content) {
      const kb = Messenger.reqres.request("active:kb:model");
      const contentId = content ? content.get("mappingId") : 0;
      let associatedContentIdSet = false;
      // Do not use walkAllResponseTemplates because that will also walk
      // response templates for decisions, and each decision can have its
      // own associated content.
      this.responseTemplates().each((responseTemplate) => {
        // Skip any new responses that are empty -- these were only added
        // because the user happened to click that locale/touchpoint combination
        if (!responseTemplate.isNew() || !responseTemplate.isEmpty()) {
          if (responseTemplate.get("associatedContentId") != contentId) {
            responseTemplate.set("associatedContentId", contentId);
            this._markResponseTemplateAsPendingIfNeeded(kb, responseTemplate);
          }
          associatedContentIdSet = true;
        }
      });
      if (!associatedContentIdSet) {
        //create a blank response on the default TP and locale on which to save the associated content
        const touchPoint = Messenger.reqres.request("default:touchpoint");
        const language = Messenger.reqres.request("default:response:language");
        let responseTemplate = this.findResponseTemplate(touchPoint, language);
        if (responseTemplate) {
          responseTemplate.set("associatedContentId", contentId);
        } else {
          this.addEmptyResponseTemplate({
            touchpointId: touchPoint,
            languageId: language,
            associatedContentId: contentId,
          });
        }
        console.log("Associated content applied to new blank response");
      }
    },
  });

  /*
   * Model for live conversations.  This is essentially a read only model, the save is used to publish a working response
   * and refresh the live conversation with the newly published version.  Changes are never made to this model directly.
   */
  TopicConversationModel.liveTopicConversation = TopicConversationModel.topicConversation.extend({
    id: 0, //give it a fake id that will force the save to use PUT, not POST.  The id is never used
    showConversation: false, //parameter for defining whether we show the live conversation or not
    needToPublish: false,
    publishAll: false,
    //override the function to get the associated content by touchpoint and locale
    getAssociatedContent: function (touchPointId, localeId) {
      const responseTemplate = this.findResponseTemplate(touchPointId, localeId);
      if (responseTemplate) {
        let associatedContent = responseTemplate.get("associatedContent");
        if (associatedContent) {
          return associatedContent;
        }
        const associatedContentId = responseTemplate.get("associatedContentId");
        if (associatedContentId > 0) {
          associatedContent = Messenger.reqres.request("content", associatedContentId);
          responseTemplate.set("associatedContent", associatedContent, { silent: true });
          return associatedContent;
        }
        return null; //no associated content on the response template
      }
    },
    // Override save function to send only the template IDs of the published responses when included
    save: function (attrs, options) {
      options = options || {};
      attrs = attrs || _.clone(this.attributes);

      // Filter the data to send to the server
      delete attrs.selected;
      delete attrs.dontSync;

      options.data = JSON.stringify(attrs);

      return Backbone.Model.prototype.save.call(this, attrs, options);
    },
    _getPublishUrl: function (publishAll) {
      const kbId = Messenger.reqres.request("active:kb");
      const intentId = this.workingConversation.get("intentId"); //get intent id from working conversation
      if (publishAll) {
        return Properties.rootURL + "/" + kbId + "/intents/" + intentId + "/conversation/publish";
      }
      return (
        Properties.rootURL +
        "/" +
        kbId +
        "/intents/" +
        intentId +
        "/conversation/publish/responseTemplates"
      );
    },
    _publishConversation: function (options) {
      const publishAll = this.publishAll;
      const templateIds = this.workingConversation._getSelectedTemplates();
      const publishOptions = _.extend({}, options, {
        contentType: "application/json",
        url: this._getPublishUrl(publishAll),
        success: (m, r) => {
          const updateResponseTemplateStatuses = (
            liveConversation,
            workingConversation,
            responses,
            decisions,
          ) => {
            const liveResponseTemplates = liveConversation
              ? liveConversation.responseTemplates()
              : null;
            const workingResponseTemplates = workingConversation
              ? workingConversation.responseTemplates()
              : null;
            _.each(responses, (response) => {
              const templateId = response.templateId;
              const liveResponseTemplate = liveResponseTemplates
                ? liveResponseTemplates.get(templateId)
                : null;
              const workingResponseTemplate = workingResponseTemplates
                ? workingResponseTemplates.get(templateId)
                : null;
              if (liveResponseTemplate) {
                liveResponseTemplate.set("statusId", response.statusId);
              }
              if (workingResponseTemplate) {
                workingResponseTemplate.set("statusId", response.statusId);
              }
            });
            _.each(decisions, (d) => {
              const liveDecision = liveConversation
                ? liveConversation.decisionCol.get(d.intentId)
                : null;
              const workingDecision = workingConversation
                ? workingConversation.decisionCol.get(d.intentId)
                : null;
              updateResponseTemplateStatuses(
                liveDecision,
                workingDecision,
                d.responses,
                d.decisions,
              );
            });
          };

          updateResponseTemplateStatuses(
            this,
            this.workingConversation,
            m.get("responses"),
            m.get("decisions"),
          );
          this.set("statusId", m.get("statusId"));
          this.set("statusVersion", m.get("statusVersion"));
          this.workingConversation.set("statusId", m.get("statusId"));
          this.workingConversation.set("statusVersion", m.get("statusVersion"));
          if (options && options.success) {
            options.success(m, r || {}, options);
          } else {
            Messenger.saveAttempted(true);
          }
        },
      });
      this.needToPublish = false;
      this.publishAll = false;
      this.workingConversation.selectedResponses = [];
      const model = new Backbone.Model({
        id: this.workingConversation.id,
      });
      if (!publishAll && templateIds && templateIds.length > 0) {
        publishOptions.data = JSON.stringify(templateIds);
      }
      model.save(null, publishOptions);
    },
    saveModel: function (options) {
      // Do not actually ever save the live conversation.
      // Override to instead publish if the conversation has been marked as needing to be published.
      const saveConversationOptions = _.extend(
        {
          error: this._createSaveModelError(options),
          success: () => {
            Messenger.saveAttempted(true);
          },
        },
        options,
      );
      if (!this.workingConversation.dirty) {
        if (this.needToPublish) {
          this._publishConversation(saveConversationOptions);
        } else {
          saveConversationOptions.success(this, null, options);
        }
      } else {
        this.workingConversation.once("sync", () => {
          this.workingConversation.off("error", null, this);
          if (this.needToPublish) {
            this._publishConversation(saveConversationOptions);
          } else {
            saveConversationOptions.success(this, null, options);
          }
        });
        this.workingConversation.once("error", (m, r) => {
          this.workingConversation.off("sync", null, this);
          saveConversationOptions.error(m, r);
        });
      }
    },
  });

  return TopicConversationModel;
});
